import type { Tab } from './index';
import { esc, timeAgo, setTableState } from '../utils';
import { workerFetch } from '../worker';
import { store } from '../state';

interface ScanMatch {
  symbol: string;
  price: number | null;
  changePct: number | null;
  gapPct: number | null;
  dayVolume: number | null;
  relVolume: number | null;
  signals: string[];
}

interface ScanResponse {
  scannedAt: string;
  count: number;
  matches: ScanMatch[];
  note?: string;
}

const SIGNAL_LABELS: Record<string, { label: string; color: string }> = {
  gainer:         { label: 'Gainer',      color: 'var(--green)'  },
  loser:          { label: 'Loser',        color: 'var(--red)'    },
  unusual_volume: { label: 'Unusual Vol',  color: 'var(--warn)'   },
  gap_up:         { label: 'Gap Up',       color: 'var(--green)'  },
  gap_down:       { label: 'Gap Down',     color: 'var(--red)'    },
  near_high:      { label: 'Near High',    color: 'var(--accent)' },
  near_low:       { label: 'Near Low',     color: 'var(--warn)'   },
};

function fmtVol(v: number | null): string {
  if (v == null) return '—';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return Math.round(v / 1e3) + 'K';
  return String(v);
}

function fmtPct(v: number | null): string {
  if (v == null) return '—';
  const s = (v > 0 ? '+' : '') + v.toFixed(2) + '%';
  const col = v > 0 ? 'var(--green)' : v < 0 ? 'var(--red)' : 'var(--text3)';
  return `<span style="color:${col};">${esc(s)}</span>`;
}

function fmtRelVol(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(1) + 'x';
}

function fmtSignals(signals: string[]): string {
  return signals.map(sig => {
    const info = SIGNAL_LABELS[sig] ?? { label: sig, color: 'var(--text3)' };
    return `<span style="display:inline-block;padding:2px 6px;border-radius:3px;font-size:10px;` +
      `letter-spacing:0.06em;border:1px solid ${info.color};color:${info.color};margin:1px 2px 1px 0;">` +
      `${esc(info.label)}</span>`;
  }).join('');
}

export class ScannerTab implements Tab {
  readonly id = 'scanner';
  private filter = 'all';

  private get section(): HTMLElement {
    return document.getElementById('tab-scanner') as HTMLElement;
  }

  init(): void {
    // Sub-nav
    document.getElementById('scanner-sub-chart')  ?.addEventListener('click', () => (window as any).showTab?.('charts'));
    document.getElementById('scanner-sub-scanner') ?.addEventListener('click', () => (window as any).showTab?.('scanner'));
    document.getElementById('scanner-sub-signals') ?.addEventListener('click', () => (window as any).showTab?.('signals'));

    // Filter buttons — use data-signal instead of inline onclick
    this.section.querySelectorAll<HTMLButtonElement>('[data-signal]').forEach(btn => {
      btn.addEventListener('click', () => this.setFilter(btn.dataset.signal ?? 'all'));
    });
    document.getElementById('scanner-refresh-btn')
      ?.addEventListener('click', () => this.fetch('GET'));
    document.getElementById('scanner-run-btn')
      ?.addEventListener('click', () => this.fetch('POST'));

    // Chart buttons — delegate so we don't need inline onclick
    document.getElementById('scanner-tbody')
      ?.addEventListener('click', e => {
        const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-chart-ticker]');
        if (!btn) return;
        const ticker = btn.dataset.chartTicker ?? '';
        // showTab initializes Charts tab synchronously (runs init()), which sets window.loadChartTicker
        (window as any).showTab?.('charts');
        (window as any).loadChartTicker?.(ticker);
      });
  }

  render(): void {
    const cached = store.get('scannerData');
    if (cached) {
      this.display(cached);
    } else {
      this.fetch('GET');
    }
  }

  private setFilter(sig: string): void {
    this.filter = sig;
    this.section.querySelectorAll<HTMLElement>('.scanner-filter-btn').forEach(b =>
      b.classList.toggle('active-filter', b.dataset.signal === sig),
    );
    const cached = store.get('scannerData');
    if (cached) this.display(cached);
  }

  private async fetch(method: 'GET' | 'POST'): Promise<void> {
    const loading    = document.getElementById('scanner-loading')!;
    const wrap       = document.getElementById('scanner-table-wrap')!;
    const empty      = document.getElementById('scanner-empty')!;
    const refreshBtn = document.getElementById('scanner-refresh-btn') as HTMLButtonElement;
    const runBtn     = document.getElementById('scanner-run-btn') as HTMLButtonElement;

    loading.style.display = '';
    wrap.style.display    = 'none';
    empty.style.display   = 'none';
    refreshBtn.disabled   = true;
    runBtn.disabled       = true;
    if (method === 'POST') runBtn.textContent = '⏳ SCANNING…';

    try {
      const data = await workerFetch<ScanResponse>('/api/scanner', { method });
      store.set('scannerData', data);
      this.display(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      loading.style.display = 'none';
      empty.style.display   = '';
      wrap.style.display    = 'none';
      (document.getElementById('scanner-empty-msg') as HTMLElement).textContent = 'Error: ' + msg;
    } finally {
      loading.style.display = 'none';
      refreshBtn.disabled   = false;
      runBtn.disabled       = false;
      runBtn.textContent    = '▶ RUN SCAN';
    }
  }

  private display(data: ScanResponse): void {
    const all     = data.matches ?? [];
    const matches = this.filter === 'all'
      ? all
      : all.filter(m => m.signals.includes(this.filter));

    // Stats
    const set = (id: string, val: string | number) =>
      (document.getElementById(id) as HTMLElement).textContent = String(val);

    set('scanner-stat-count',   all.length);
    set('scanner-stat-gainers', all.filter(m => m.signals.includes('gainer')).length);
    set('scanner-stat-losers',  all.filter(m => m.signals.includes('loser')).length);
    set('scanner-stat-vol',     all.filter(m => m.signals.includes('unusual_volume')).length);
    set('scanner-stat-time',    data.scannedAt ? new Date(data.scannedAt).toLocaleTimeString() : '—');

    const ageEl = document.getElementById('scanner-scan-age');
    if (ageEl) ageEl.textContent = data.scannedAt ? 'last scan ' + timeAgo(data.scannedAt) : 'no scan yet';

    const emptyMsg = document.getElementById('scanner-empty-msg') as HTMLElement;
    emptyMsg.textContent = this.filter === 'all'
      ? (data.note ?? 'No matches — scanner runs every 5 min during market hours, or click Refresh.')
      : 'No matches for this signal filter.';

    setTableState('scanner-tbody', 'scanner-table-wrap', 'scanner-empty', matches, (m: ScanMatch) =>
      `<tr>
        <td style="font-weight:700;color:var(--accent);letter-spacing:0.05em;white-space:nowrap;">
          ${esc(m.symbol)}
          <button data-chart-ticker="${esc(m.symbol)}"
            title="Open chart for ${esc(m.symbol)}"
            style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:1px 5px;cursor:pointer;border-radius:2px;margin-left:5px;vertical-align:middle;">📈</button>
        </td>
        <td style="font-family:var(--mono);">$${esc(m.price != null ? m.price.toFixed(2) : '—')}</td>
        <td style="font-family:var(--mono);">${fmtPct(m.changePct)}</td>
        <td style="font-family:var(--mono);">${fmtPct(m.gapPct)}</td>
        <td style="font-family:var(--mono);color:var(--text2);">${esc(fmtVol(m.dayVolume))}</td>
        <td style="font-family:var(--mono);color:var(--warn);">${esc(fmtRelVol(m.relVolume))}</td>
        <td>${fmtSignals(m.signals ?? [])}</td>
      </tr>`,
    );
  }
}
